import React, { useState, useEffect, useRef } from "react";
import ApiServices from "../services/ApiServices";
import { ShowSuccessToast, ShowDangerToast } from "../services/toast";
import { Download, Phone, MapPin, X, Image, Package, Search, List, LayoutGrid, Calendar, Check, MoreHorizontal, Plus, Upload, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";

const statusLabelMap = {
  pending: "Needs Review",
  assigned: "Ready",
  completed: "Completed",
  rejected: "Rejected",
};

const statusBadgeClass = (status) => {
  const map = {
    pending: "bg-orange-50 text-orange-700 ring-1 ring-orange-600/20",
    assigned: "bg-green-50 text-green-700 ring-1 ring-green-600/20",
    completed: "bg-green-50 text-green-700 ring-1 ring-green-600/20",
    rejected: "bg-red-50 text-red-700 ring-1 ring-red-600/20",
  };
  return map[status] || "bg-gray-100 text-gray-700";
};

const statusIcon = (status) => {
  if (status === "pending") return <span className="text-orange-500 mr-1">⊘</span>;
  if (status === "assigned") return <span className="text-green-500 mr-1">◉</span>;
  if (status === "completed") return <Check className="w-3 h-3 text-green-600 mr-1" />;
  return null;
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return "";
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return "";
  const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${days[dt.getDay()]}, ${months[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
};

const DonationRequest = ({ storeId, store, profile, initialStatusFilter }) => {
  const [donationRequests, setDonationRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, assigned: 0, completed: 0, rejected: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState({});
  const [imageModal, setImageModal] = useState({ open: false, imageUrl: "", alt: "" });
  const [submittingItem, setSubmittingItem] = useState(false);

  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter || "");
  const [windowFilter, setWindowFilter] = useState("");
  const [driverFilter, setDriverFilter] = useState("");
  const [detailPanel, setDetailPanel] = useState(null);
  const [overflowMenu, setOverflowMenu] = useState(null);
  const overflowRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;

  const [rejectItemModal, setRejectItemModal] = useState({
    open: false,
    requestId: null,
    itemIds: [],
    rejectionReason: "",
    itemName: "",
  });

  const [showManualForm, setShowManualForm] = useState(false);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  const [manualForm, setManualForm] = useState({
    donorName: "",
    donorPhone: "",
    donorEmail: "",
    pickupAddress: "",
    specialNote: "",
    businessName: "",
    pickUpType: "Residential",
    pickUpTime: "AM",
    pickupDate: new Date().toISOString().split("T")[0],
    enableReminders: false,
    items: [{ name: "", description: "", image: null, imagePreview: null }],
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [pickupLatLng, setPickupLatLng] = useState({ latitude: null, longitude: null });
  const itemFileRefs = useRef([]);

  useEffect(() => {
    if (initialStatusFilter !== undefined) {
      setStatusFilter(initialStatusFilter || '');
      setCurrentPage(1);
    }
  }, [initialStatusFilter]);

  const MAPBOX_TOKEN = "pk.eyJ1IjoiZG9ub3JkaXNwYXRjaCIsImEiOiJjbWMwdjZrZWIwNnU2MmxwdHBsaGkzcm9rIn0.u70VsaQCG6q1r8SLZ8nVLg";

  const resetManualForm = () => {
    setManualForm({
      donorName: "",
      donorPhone: "",
      donorEmail: "",
      pickupAddress: "",
      specialNote: "",
      businessName: "",
      pickUpType: "Residential",
      pickUpTime: "AM",
      pickupDate: new Date().toISOString().split("T")[0],
      enableReminders: false,
      items: [{ name: "", description: "", image: null, imagePreview: null }],
    });
    setAddressSuggestions([]);
    setPickupLatLng({ latitude: null, longitude: null });
  };

  const handleManualSubmit = async () => {
    if (!manualForm.donorName.trim()) return ShowDangerToast("Donor name is required");
    if (!manualForm.donorPhone.trim()) return ShowDangerToast("Phone number is required");
    if (!manualForm.pickupDate) return ShowDangerToast("Please select a pickup date");
    if (manualForm.items.length === 0 || manualForm.items.every(i => !i.name.trim()))
      return ShowDangerToast("At least one item with a name is required");
    if (manualForm.items.some(item => item.name.trim() && !item.image))
      return ShowDangerToast("Every item with a name must have an image");
    if (manualForm.pickUpType === "Corporate" && !manualForm.businessName.trim())
      return ShowDangerToast("Business name is required for Corporate pickups");
    if (!pickupLatLng.latitude || !pickupLatLng.longitude)
      return ShowDangerToast("Please select a valid pickup address from the suggestions");

    setIsSubmittingManual(true);
    try {
      const formData = new FormData();
      formData.append("storeId", storeId);
      formData.append("donorName", manualForm.donorName.trim());
      formData.append("phone", manualForm.donorPhone.trim());
      formData.append("address", manualForm.pickupAddress?.trim() || store?.address || "");
      formData.append("latitude", String(pickupLatLng.latitude));
      formData.append("longitude", String(pickupLatLng.longitude));
      formData.append("pickupDate", manualForm.pickupDate);
      formData.append("pickUpTime", manualForm.pickUpTime || "AM");
      formData.append("pickUpType", manualForm.pickUpType || "Residential");
      formData.append("enableReminders", manualForm.enableReminders ? true : false);
      formData.append("email", manualForm.donorEmail.trim() || "");
      formData.append("businessName", manualForm.businessName.trim() || "");
      formData.append("specialNote", manualForm.specialNote.trim() || "");

      manualForm.items.forEach((item, index) => {
        if (!item.name.trim()) return;
        formData.append("items", JSON.stringify({
          name: item.name.trim(),
          description: item.description?.trim() || "",
          index: index,
        }));
        if (item.image) {
          formData.append(`item-${item.name.trim()}-0`, item.image);
        }
      });

      await ApiServices.postFormData("/donations", formData);
      ShowSuccessToast("Manual pickup created successfully!");
      setShowManualForm(false);
      resetManualForm();
      fetchDonationRequests();
    } catch (err) {
      console.error("Manual pickup submit error:", err);
      ShowDangerToast("Failed to create pickup: " + (err?.response?.data?.error || err.message || "Unknown error"));
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const handleManualPhoneChange = (e) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val && !val.startsWith("1")) val = "1" + val;
    if (val.length > 11) val = val.slice(0, 11);
    setManualForm({ ...manualForm, donorPhone: val ? `+${val}` : "" });
  };

  const handleAddressSearch = async (query) => {
    setManualForm({ ...manualForm, pickupAddress: query });
    setPickupLatLng({ latitude: null, longitude: null });
    if (query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    try {
      const res = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&country=us&limit=5`
      );
      const data = await res.json();
      setAddressSuggestions(data.features || []);
    } catch (err) {
      console.error("Address search error:", err);
    }
  };

  const handleSelectAddress = (feat) => {
    setManualForm({ ...manualForm, pickupAddress: feat.place_name });
    setPickupLatLng({ latitude: feat.center[1], longitude: feat.center[0] });
    setAddressSuggestions([]);
  };

  const handleItemChange = (idx, field, value) => {
    const newItems = [...manualForm.items];
    newItems[idx][field] = value;
    setManualForm({ ...manualForm, items: newItems });
  };

  const handleItemImageChange = (idx, file) => {
    if (!file) return;
    const newItems = [...manualForm.items];
    newItems[idx].image = file;
    newItems[idx].imagePreview = URL.createObjectURL(file);
    setManualForm({ ...manualForm, items: newItems });
  };

  const addItem = () => {
    setManualForm({ ...manualForm, items: [...manualForm.items, { name: "", description: "", image: null, imagePreview: null }] });
  };

  const removeItem = (idx) => {
    setManualForm({ ...manualForm, items: manualForm.items.filter((_, i) => i !== idx) });
  };

  useEffect(() => {
    const loadInit = async () => {
      await fetchDrivers();
      await fetchDonationRequests();
    };
    loadInit();
  }, [storeId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target)) {
        setOverflowMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchDonationRequests = async () => {
    setIsLoading(true);
    try {
      let url = `/donations/new/${storeId}`;
      const response = await ApiServices.getData(url);
      setDonationRequests(response.data.list || []);
      setCounts(
        response.data.counts && typeof response.data.counts === "object"
          ? response.data.counts
          : { pending: 0, assigned: 0, completed: 0, rejected: 0 }
      );
    } catch (error) {
      console.error("Fetch donation requests error:", error);
      ShowDangerToast("Failed to fetch donation requests");
      setDonationRequests([]);
      setCounts({ pending: 0, assigned: 0, completed: 0, rejected: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const res = await ApiServices.getDrivers(storeId);
      setDrivers(res);
    } catch {
      console.error("Failed to fetch drivers");
    }
  };

  const handleAssignDriver = async (donationId, driverId) => {
    if (!driverId) {
      ShowDangerToast("Please select a driver");
      return;
    }
    try {
      await ApiServices.assignDriver(donationId, driverId);
      ShowSuccessToast("Driver assigned successfully");
      fetchDonationRequests();
    } catch {
      ShowDangerToast("Failed to assign driver");
    }
  };

  const handleMarkCompleted = async (requestId) => {
    if (!window.confirm('Are you sure you want to mark this pickup as completed? This action cannot be undone.')) return;
    try {
      const req = donationRequests.find((r) => r.id === requestId);
      if (!req) return;
      const updatedItems = req.DonationItems
        ? req.DonationItems.map((item) => ({
          id: item.id,
          status: "completed",
          rejectionReason: "",
        }))
        : [];
      await ApiServices.postData("/donations/updateDonationStatus", {
        requestId,
        requestStatus: "completed",
        items: JSON.stringify(updatedItems),
      });
      ShowSuccessToast("Pickup marked as completed");
      setDetailPanel(null);
      fetchDonationRequests();
    } catch {
      ShowDangerToast("Failed to mark as completed");
    }
  };

  const handleApproveItem = async (requestId, itemIds) => {
    try {
      await ApiServices.postData("/donations/updateDonationStatus", {
        requestId,
        items: JSON.stringify(itemIds.map((id) => ({ id, status: "completed", rejectionReason: "" }))),
      });
      ShowSuccessToast("Item approved");
      const resp = await ApiServices.getData(`/donations/new/${storeId}`);
      const list = resp.data.list || [];
      setDonationRequests(list);
      const updated = list.find((r) => r.id === requestId);
      if (updated) setDetailPanel(updated);
    } catch {
      ShowDangerToast("Failed to approve item");
    }
  };

  const handleRejectItem = (requestId, itemIds, itemName) => {
    setRejectItemModal({
      open: true,
      requestId,
      itemIds,
      rejectionReason: "",
      itemName,
    });
  };

  const submitRejectItem = async () => {
    if (!window.confirm(`Are you sure you want to reject "${rejectItemModal.itemName}"?`)) return;
    try {
      setSubmittingItem(true);
      await ApiServices.postData("/donations/updateDonationStatus", {
        requestId: rejectItemModal.requestId,
        items: JSON.stringify(
          rejectItemModal.itemIds.map((id) => ({
            id,
            status: "rejected",
            rejectionReason: rejectItemModal.rejectionReason || "No reason provided",
          }))
        ),
      });
      ShowSuccessToast(`Item "${rejectItemModal.itemName}" rejected`);
      setRejectItemModal({ open: false, requestId: null, itemIds: [], rejectionReason: "", itemName: "" });
      fetchDonationRequests();
    } catch {
      ShowDangerToast("Failed to reject item");
    } finally {
      setSubmittingItem(false);
    }
  };

  const handleExport = () => {
    const exportData = donationRequests.map((donation) => ({
      "Donor Name": donation.donorName,
      "Donor Phone": donation.donorPhone,
      "Donor Email": donation.donorEmail,
      Address: donation.address,
      "Pickup Date": formatDateShort(donation.pickupDate),
      "Pickup Time": donation.pickupTime,
      Status: statusLabelMap[donation.status] || donation.status,
      Driver: donation.driver?.fullName || "Unassigned",
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pickups");
    XLSX.writeFile(workbook, `Pickups_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handleImageClick = (url, alt) => {
    setImageModal({ open: true, imageUrl: url, alt });
  };

  const totalCount = counts.pending + counts.assigned + counts.completed + counts.rejected;

  const allFilteredRequests = donationRequests.filter((req) => {
    if (statusFilter && req.status !== statusFilter) return false;
    if (windowFilter && req.pickupTime !== windowFilter) return false;
    if (driverFilter && String(req.driverId) !== driverFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match =
        (req.donorName || "").toLowerCase().includes(q) ||
        (req.donorPhone || "").toLowerCase().includes(q) ||
        (req.address || "").toLowerCase().includes(q) ||
        (req.donorEmail || "").toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(allFilteredRequests.length / ITEMS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  if (safePage !== currentPage) {
    setTimeout(() => setCurrentPage(safePage), 0);
  }
  const filteredRequests = allFilteredRequests.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE
  );

  const openDetail = (req) => {
    setDetailPanel(req);
    setOverflowMenu(null);
  };

  const renderStatusBadge = (status) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusBadgeClass(status)}`}>
      {statusIcon(status)}
      {statusLabelMap[status] || status}
    </span>
  );

  const renderListView = () => (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Donor</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
              <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredRequests.map((req) => (
              <tr
                key={req.id}
                className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                onClick={() => openDetail(req)}
                data-donation-id={req.id}
              >
                <td className="py-3 px-4">
                  <p className="text-sm font-semibold text-gray-900">{req.donorName}</p>
                  <p className="text-xs text-gray-400">{req.donorPhone}</p>
                </td>
                <td className="py-3 px-4">
                  <p className="text-sm text-gray-600 max-w-[220px] truncate">{req.address}</p>
                </td>
                <td className="py-3 px-4">
                  <p className="text-sm text-gray-700 whitespace-nowrap">{formatDateShort(req.pickupDate)}</p>
                  {req.pickupTime && (
                    <span className="inline-block mt-0.5 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium">
                      {req.pickupTime}
                    </span>
                  )}
                </td>
                <td className="py-3 px-4">{renderStatusBadge(req.status)}</td>
                <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                  {req.driver?.fullName ? (
                    <span className="text-sm text-gray-700">{req.driver.fullName}</span>
                  ) : (
                    <select
                      value={selectedDrivers[req.id] ?? req.driverId ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedDrivers((prev) => ({ ...prev, [req.id]: val }));
                        if (val) handleAssignDriver(req.id, val);
                      }}
                      className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 min-w-[140px]"
                    >
                      <option value="">Assign driver</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.fullName}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td className="py-3 px-4 text-right relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setOverflowMenu(overflowMenu === req.id ? null : req.id)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {overflowMenu === req.id && (
                    <div ref={overflowRef} className="absolute right-4 top-10 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 min-w-[140px]">
                      <button onClick={() => openDetail(req)} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        View Details
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderGridView = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredRequests.map((req) => (
        <div
          key={req.id}
          className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => openDetail(req)}
          data-donation-id={req.id}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-900">{req.donorName}</p>
              <p className="text-xs text-gray-400">{req.donorPhone}</p>
            </div>
            {renderStatusBadge(req.status)}
          </div>
          <div className="space-y-1.5 mt-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{req.address}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-600">
              <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span>{formatDateShort(req.pickupDate)}</span>
              {req.pickupTime && <span>• {req.pickupTime}</span>}
            </div>
            {req.driver?.fullName && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <span className="w-3.5 h-3.5 flex items-center justify-center text-gray-400 flex-shrink-0">👤</span>
                <span>{req.driver.fullName}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  const renderDetailPanel = () => {
    if (!detailPanel) return null;
    const req = donationRequests.find((r) => r.id === detailPanel.id) || detailPanel;

    const groupedItems = new Map();
    if (req.DonationItems) {
      req.DonationItems.forEach((item) => {
        const key = `${(item.name || "").toLowerCase()}|${(item.description || "").toLowerCase()}|${item.status}`;
        if (!groupedItems.has(key)) {
          groupedItems.set(key, { ...item, quantity: 0, itemIds: [] });
        }
        const group = groupedItems.get(key);
        group.quantity += 1;
        group.itemIds.push(item.id);
      });
    }
    const items = Array.from(groupedItems.values());

    return (
      <>
        <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setDetailPanel(null)} />
        <div className="fixed top-0 right-0 h-full w-full max-w-[380px] bg-white z-50 shadow-2xl flex flex-col overflow-hidden animate-slide-in">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">Pickup Details</h2>
            <button onClick={() => setDetailPanel(null)} className="p-1 rounded-full hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
            <div>
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">Donor Information</p>
              <p className="text-lg font-bold text-gray-900">{req.donorName}</p>
              <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{req.donorPhone}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-sm text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{req.address}</span>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">Schedule</p>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-800 font-medium">{formatDateLong(req.pickupDate)}</span>
                {req.pickupTime && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                    {req.pickupTime}
                  </span>
                )}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">Status & Assignment</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  {renderStatusBadge(req.status)}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Driver</span>
                  {req.driver?.fullName ? (
                    <span className="text-sm font-medium text-gray-800">{req.driver.fullName}</span>
                  ) : (
                    <select
                      value={selectedDrivers[req.id] ?? req.driverId ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedDrivers((prev) => ({ ...prev, [req.id]: val }));
                        if (val) handleAssignDriver(req.id, val);
                      }}
                      className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                    >
                      <option value="">Assign driver</option>
                      {drivers.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.fullName}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">
                Items ({req.DonationItems ? req.DonationItems.length : 0})
              </p>
              {items.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">No items</p>
              ) : (
                <div className="space-y-3">
                  {items.map((item) => (
                    <div key={item.itemIds[0]} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          onClick={() => handleImageClick(item.imageUrl, item.name)}
                          className="w-12 h-12 rounded-lg object-cover cursor-pointer flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <Image className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-800 truncate">{item.name}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusBadgeClass(item.status)}`}>
                            {statusLabelMap[item.status] || item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                          </span>
                        </div>
                        {item.status !== "completed" && item.status !== "rejected" && (
                          <div className="flex items-center gap-2 mt-2">
                            <button
                              onClick={() => handleApproveItem(req.id, item.itemIds)}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Check className="w-3 h-3" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleRejectItem(req.id, item.itemIds, item.name)}
                              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <X className="w-3 h-3" />
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-4 border-t border-gray-200 flex items-center gap-3">
            <button
              onClick={() => handleMarkCompleted(req.id)}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors"
            >
              <Check className="w-4 h-4" />
              Mark Completed
            </button>
            <button
              onClick={() => setDetailPanel(null)}
              className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.25s ease-out;
        }
      `}</style>

      <div className="w-full mx-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-xl font-bold text-navy-800">Pickups</h2>
            <p className="text-sm text-gray-500">{totalCount} total pickups</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button onClick={() => { resetManualForm(); setShowManualForm(true); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm">
              <Plus className="w-4 h-4" />
              Manual Pickup
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, phone, address..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 min-w-[120px]"
          >
            <option value="">All Status</option>
            <option value="pending">Needs Review</option>
            <option value="assigned">Ready</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={windowFilter}
            onChange={(e) => { setWindowFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 min-w-[130px]"
          >
            <option value="">All Windows</option>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
          <select
            value={driverFilter}
            onChange={(e) => { setDriverFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 min-w-[130px]"
          >
            <option value="">All Drivers</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.fullName}
              </option>
            ))}
          </select>
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2.5 transition-colors ${viewMode === "list" ? "bg-gray-100 text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2.5 transition-colors ${viewMode === "grid" ? "bg-gray-100 text-gray-900" : "text-gray-400 hover:text-gray-600"}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-[3px] border-green-200 border-t-green-600 rounded-full animate-spin" />
          </div>
        )}
        {!isLoading && filteredRequests.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No pickups found</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchQuery || statusFilter || windowFilter || driverFilter
                ? "Try adjusting your filters or search query."
                : "Create a new manual pickup to get started."}
            </p>
          </div>
        )}
        {!isLoading && filteredRequests.length > 0 && (
          viewMode === "list" ? renderListView() : renderGridView()
        )}
        {!isLoading && totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-3 mt-4">
            <p className="text-sm text-gray-500">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, allFilteredRequests.length)} of {allFilteredRequests.length}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`dot-${i}`} className="px-2 text-gray-400 text-sm">...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === p
                          ? 'bg-navy-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                        }`}
                    >
                      {p}
                    </button>
                  )
                )
              }
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {renderDetailPanel()}

      {showManualForm && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowManualForm(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl animate-slide-in overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-navy-800">New Manual Pickup</h3>
                <p className="text-xs text-gray-500">Fill in the details below to create a pickup</p>
              </div>
              <button onClick={() => setShowManualForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Donor Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Enter donor's full name"
                  value={manualForm.donorName}
                  onChange={(e) => setManualForm({ ...manualForm, donorName: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={manualForm.donorPhone}
                    onChange={handleManualPhoneChange}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    placeholder="donor@email.com"
                    value={manualForm.donorEmail}
                    onChange={(e) => setManualForm({ ...manualForm, donorEmail: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Pickup Date <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={manualForm.pickupDate}
                    onChange={(e) => setManualForm({ ...manualForm, pickupDate: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Pickup Window <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    {["AM", "PM"].map((time) => (
                      <button
                        key={time}
                        type="button"
                        onClick={() => setManualForm({ ...manualForm, pickUpTime: time })}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${manualForm.pickUpTime === time
                            ? "bg-green-50 border-2 border-green-500 text-green-700"
                            : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                          }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Pickup Type <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  {["Residential", "Corporate"].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setManualForm({ ...manualForm, pickUpType: type })}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${manualForm.pickUpType === type
                          ? "bg-navy-50 border-2 border-navy-500 text-navy-700"
                          : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                        }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {manualForm.pickUpType === "Corporate" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Business Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter business name"
                    value={manualForm.businessName}
                    onChange={(e) => setManualForm({ ...manualForm, businessName: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Pickup Address <span className="text-red-500">*</span></label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Start typing address..."
                    value={manualForm.pickupAddress}
                    onChange={(e) => handleAddressSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                  />
                  {addressSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg max-h-[200px] overflow-y-auto z-20 shadow-lg">
                      {addressSuggestions.map((feat, i) => (
                        <div
                          key={i}
                          onClick={() => handleSelectAddress(feat)}
                          className="px-4 py-2.5 text-sm cursor-pointer hover:bg-green-50 transition-colors border-b border-gray-50 last:border-0"
                        >
                          {feat.place_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {pickupLatLng.latitude && (
                  <p className="text-xs text-green-600 mt-1">Address selected ({pickupLatLng.latitude.toFixed(4)}, {pickupLatLng.longitude.toFixed(4)})</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Donated Items <span className="text-red-500">*</span></label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="inline-flex items-center gap-1 text-xs font-medium text-green-600 hover:text-green-700 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Item
                  </button>
                </div>
                {manualForm.items.map((item, idx) => (
                  <div key={idx} className="border border-dashed border-gray-300 rounded-lg p-4 mb-3 bg-gray-50 relative">
                    <input
                      type="text"
                      placeholder="Item name *"
                      value={item.name}
                      onChange={(e) => handleItemChange(idx, "name", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white"
                    />
                    <textarea
                      placeholder="Description (optional)"
                      value={item.description}
                      onChange={(e) => handleItemChange(idx, "description", e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 resize-none focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 bg-white"
                    />
                    <div className="flex items-center gap-3">
                      <input
                        ref={(el) => (itemFileRefs.current[idx] = el)}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleItemImageChange(idx, e.target.files[0])}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => itemFileRefs.current[idx]?.click()}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {item.image ? "Change Photo" : "Add Photo *"}
                      </button>
                      {item.imagePreview && (
                        <img src={item.imagePreview} alt="preview" className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                      )}
                    </div>
                    {manualForm.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(idx)}
                        className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Special Notes</label>
                <textarea
                  placeholder="Add any special notes about this pickup..."
                  value={manualForm.specialNote}
                  onChange={(e) => setManualForm({ ...manualForm, specialNote: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={manualForm.enableReminders}
                  onChange={(e) => setManualForm({ ...manualForm, enableReminders: e.target.checked })}
                  className="w-4 h-4 accent-green-600 cursor-pointer"
                />
                <span className="text-sm text-gray-600">Send reminder to donor before pickup</span>
              </label>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center gap-3 justify-end">
              <button
                onClick={() => setShowManualForm(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleManualSubmit}
                disabled={isSubmittingManual}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmittingManual ? "Creating..." : "Create Pickup"}
              </button>
            </div>
          </div>
        </div>
      )}

      {imageModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setImageModal({ open: false, imageUrl: "", alt: "" })}>
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setImageModal({ open: false, imageUrl: "", alt: "" })} className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg">
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <img src={imageModal.imageUrl} alt={imageModal.alt} className="max-w-full max-h-[85vh] rounded-xl" />
          </div>
        </div>
      )}

      {rejectItemModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reject Item</h3>
            <p className="text-sm text-gray-600 mb-4">Reject "{rejectItemModal.itemName}"</p>
            <textarea
              placeholder="Reason for rejection (optional)"
              value={rejectItemModal.rejectionReason}
              onChange={(e) => setRejectItemModal({ ...rejectItemModal, rejectionReason: e.target.value })}
              className="w-full border border-gray-200 rounded-lg p-3 text-sm mb-4 focus:outline-none focus:ring-1 focus:ring-green-500"
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setRejectItemModal({ open: false, requestId: null, itemIds: [], rejectionReason: "", itemName: "" })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={submitRejectItem}
                disabled={submittingItem}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {submittingItem ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DonationRequest;